import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import WorkerProfile from './WorkerProfile';
import FeatureBox from './FeatureBox';
import { motion, AnimatePresence } from 'framer-motion';

// Theme variables for spacing control
const theme = {
  spacing: {
    titleToSubtitle: '32px',
    subtitleToDepartments: '64px',
    departmentToWorkers: '48px',
    sectionBottom: '0px',
  },
  sectionGap: '32px',
  rightSectionTopMargin: '4px',
  featureBoxHorizontalGap: '24px',
  featureBoxVerticalGap: '40px',
  contentTopMargin: '48px',
  workerCard: {
    background: {
      active: 'rgba(0, 0, 0, 0.5)',
      inactive: 'rgba(255, 255, 255, 0.3)',
      activeHover: 'rgba(0, 0, 0, 0.2)',
      inactiveHover: 'rgba(255, 255, 255, 0.1)',
    },
    text: {
      active: '#ffffff',
      inactive: '#000000',
      activeOpacity: 1,
      inactiveOpacity: 0.9,
    },
    border: {
      active: 'rgba(255, 255, 255, 0.2)',
      inactive: 'rgba(0, 0, 0, 0.1)',
    },
    glass: {
      blur: '12px',
      opacity: 0.6,
    },
    hover: {
      transform: 'scale(1.02) translateY(-4px)',
      transition: 'all 0.3s cubic-bezier(0.3, 0, 0.2, 1)',
      shadow: '0 20px 40px rgba(0, 0, 0, 0.2), 0 15px 20px rgba(0, 0, 0, 0.1)',
    },
    gradient: {
      active: 'rgba(0, 0, 0, 0.8)',
      inactive: 'rgb(255, 255, 255)',
      opacity: {
        active: 0.8,
        inactive: 0.5,
      }
    },
    image: {
      opacity: {
        active: 0.7,
        inactive: 0.3,
        activeHover: 0.6,
        inactiveHover: 0.5,
      }
    }
  },
};

const Section = styled.section`
  width: 100%;
  padding-top: 90px;
  padding-bottom: ${props => props.theme.spacing.sectionBottom};
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 72px;
  font-weight: 600;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.titleToSubtitle};
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 20px;
  color: #666;
  margin-bottom: ${props => props.theme.spacing.subtitleToDepartments};
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

const WorkerSelector = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin: 0 15px 48px 15px;
  position: relative;
`;

const WorkerCard = styled(motion.div)`
  position: relative;
  width: 400px;
  height: 160px;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  background: ${props => props.isActive 
    ? props.theme.workerCard.background.active 
    : props.theme.workerCard.background.inactive};
  backdrop-filter: blur(${props => props.theme.workerCard.glass.blur});
  -webkit-backdrop-filter: blur(${props => props.theme.workerCard.glass.blur});
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: ${props => props.$isActive 
    ? props.theme.workerCard.text.active 
    : props.theme.workerCard.text.inactive};
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, 
      ${props => props.isActive 
        ? props.theme.workerCard.gradient.active 
        : props.theme.workerCard.gradient.inactive}, 
      transparent);
    opacity: ${props => props.isActive 
      ? props.theme.workerCard.gradient.opacity.active 
      : props.theme.workerCard.gradient.opacity.inactive};
    mix-blend-mode: overlay;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover {
    transform: ${props => props.theme.workerCard.hover.transform};
    box-shadow: ${props => props.theme.workerCard.hover.shadow};
    background: ${props => props.$isActive 
      ? props.theme.workerCard.background.activeHover 
      : props.theme.workerCard.background.inactiveHover};
  }
`;

const WorkerImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${props => `url(${props.$image})`};
  background-size: cover;
  background-position: center;
  opacity: ${props => props.$isActive 
    ? props.theme.workerCard.image.opacity.active 
    : props.theme.workerCard.image.opacity.inactive};
  transition: opacity 0.3s ease;
  z-index: 0;

  ${WorkerCard}:hover & {
    opacity: ${props => props.$isActive 
      ? props.theme.workerCard.image.opacity.activeHover 
      : props.theme.workerCard.image.opacity.inactiveHover};
  }
`;

const WorkerContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 0, 0, 0.2)
  );
`;

const WorkerRole = styled.h3`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  opacity: ${props => props.$isActive ? 1 : 0.8};
  text-shadow: ${props => props.$isActive 
    ? '0 2px 4px rgba(0,0,0,0.2)' 
    : 'none'};
`;

const WorkerName = styled.p`
  font-size: 16px;
  margin: 4px 0 0 0;
  opacity: ${props => props.$isActive ? 0.9 : 0.6};
  text-shadow: ${props => props.$isActive 
    ? '0 1px 2px rgba(0,0,0,0.2)' 
    : 'none'};
  
  &::before {
    content: 'Meet ';
  }
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.sectionGap};
  margin-top: ${props => props.theme.contentTopMargin};
  justify-content: center;
  max-width: 1100px;
  margin-left: 15px;
  margin-right: auto;
  margin-bottom: 80px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 48px;
  }
`;

const MainCardWrapper = styled.div`
  flex: 0 0 45%;
  
  @media (max-width: 1024px) {
    flex: none;
  }
`;

const FeaturesWrapper = styled.div`
  flex: 0 0 55%;
  margin-top: ${props => props.theme.rightSectionTopMargin};
  padding: 0 5px 0 0px;
  
  @media (max-width: 1024px) {
    flex: none;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: ${props => props.theme.featureBoxHorizontalGap};
  row-gap: ${props => props.theme.featureBoxVerticalGap};
  margin: 12px -8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const DepartmentSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: ${props => props.theme.spacing.departmentToWorkers};
  margin-bottom: ${props => props.theme.spacing.departmentToWorkers};
  position: relative;
  padding: 4px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const DepartmentButton = styled.button`
  padding: 12px 24px;
  border-radius: 16px;
  background: ${props => props.$isActive 
    ? 'rgba(0, 0, 0, 0.95)' 
    : 'transparent'};
  color: ${props => props.$isActive ? '#ffffff' : 'rgba(0, 0, 0, 0.7)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 16px;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.$isActive 
      ? 'rgba(0, 0, 0, 0.95)' 
      : 'rgba(0, 0, 0, 0.05)'};
    color: ${props => props.$isActive ? '#ffffff' : 'rgba(0, 0, 0, 0.9)'};
  }
`;

const DigitalWorkersV2 = () => {
  const { t, tSafe, isLoading, isContentReady } = useLanguage();
  const [activeDepartment, setActiveDepartment] = useState('marketing');
  const [activeWorker, setActiveWorker] = useState('aria');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setIsHovered(false);
  }, [activeWorker]);

  // Enhanced debugging for departmentWorkers
  const departmentWorkers = useMemo(() => {
    const workers = tSafe(`digitalWorkers.departments.${activeDepartment}.workers`, 'array');
    
    if (!Array.isArray(workers)) {
      return []; // Safe fallback
    }
    return workers;
  }, [activeDepartment, tSafe]);

  // Enhanced debugging for departments
  const departments = useMemo(() => {
    const deptData = tSafe('digitalWorkers.departments', 'object');
    
    if (!deptData || typeof deptData !== 'object') {
      return {}; // Safe fallback
    }
    
    return deptData;
  }, [tSafe]);

  const handleWorkerClick = (worker) => {
    setActiveWorker(worker);
  };

  const handleDepartmentClick = (department) => {
    setActiveDepartment(department);
    const workers = tSafe(`digitalWorkers.departments.${department}.workers`, 'array');
    
    if (Array.isArray(workers) && workers.length > 0) {
      setActiveWorker(workers[0]);
    }
  };

  // Enhanced renderWorkerSection with error boundaries
  const renderWorkerSection = (workerKey) => {
    try {
      
      return (
        <ContentWrapper
          theme={theme}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <MainCardWrapper>
            <WorkerProfile 
              workerKey={workerKey} 
              shouldPlayVideo={isHovered}
            />
          </MainCardWrapper>
          
          <FeaturesWrapper theme={theme}>
            <FeaturesGrid theme={theme}>
              {[1, 2, 3, 4].map((num) => {
                try {
                  const title = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.title`);
                  const description = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.description`);
                  const image = t(`digitalWorkers.workers.${workerKey}.features.feature${num}.image`);
                  
                  /* log removed */
                  
                  return (
                    <FeatureBox
                      key={num}
                      title={title}
                      description={description}
                      image={image}
                    />
                  );
                } catch (error) {
                  /* log removed */
                  return (
                    <div key={num} style={{ padding: '20px', background: '#ffe6e6', borderRadius: '8px' }}>
                      <strong>Error loading feature {num}</strong>
                      <pre>{error.message}</pre>
                    </div>
                  );
                }
              })}
            </FeaturesGrid>
          </FeaturesWrapper>
        </ContentWrapper>
      );
    } catch (error) {
      /* log removed */
      return (
        <div style={{ padding: '40px', background: '#ffe6e6', borderRadius: '8px', margin: '20px' }}>
          <h3>Error rendering worker section</h3>
          <p><strong>Worker:</strong> {workerKey}</p>
          <p><strong>Error:</strong> {error.message}</p>
          <pre>{error.stack}</pre>
        </div>
      );
    }
  };

  // Add loading state display
  if (isLoading || !isContentReady) {
    return (
      <Section id="digitalWorkers" theme={theme}>
        <Container>
          <Title theme={theme}>Loading Digital Workers...</Title>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p>Loading translations...</p>
            <p>isLoading: {isLoading.toString()}</p>
            <p>isContentReady: {isContentReady.toString()}</p>
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <Section id="digitalWorkers" theme={theme}>
      <Container>
        <Title theme={theme}>{t('digitalWorkers.title')}</Title>
        <Subtitle theme={theme}>{t('digitalWorkers.subtitle')}</Subtitle>
        
        <DepartmentSelector theme={theme}>
          {Object.keys(departments).map((department) => {
            try {
              return (
                <DepartmentButton
                  key={department}
                  $isActive={activeDepartment === department}
                  onClick={() => handleDepartmentClick(department)}
                >
                  {t(`digitalWorkers.departments.${department}.name`)}
                </DepartmentButton>
              );
            } catch (error) {
              /* log removed */
              return (
                <div key={department} style={{ padding: '10px', background: '#ffe6e6' }}>
                  Error: {department}
                </div>
              );
            }
          })}
        </DepartmentSelector>
        
        <WorkerSelector>
          {departmentWorkers.map((worker) => {
            try {
              return (
                <WorkerCard
                  key={worker}
                  className="worker-card-hover"
                  $isActive={activeWorker === worker}
                  onClick={() => handleWorkerClick(worker)}
                  onMouseEnter={() => worker === activeWorker && setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  theme={theme}
                >
                  <WorkerImage
                    $image={t(`digitalWorkers.workers.${worker}.profile.media.image`)}
                    $isActive={activeWorker === worker}
                    theme={theme}
                  />
                  <WorkerContent>
                    <div>
                      <WorkerRole $isActive={activeWorker === worker}>
                        {t(`digitalWorkers.workers.${worker}.profile.role`)}
                      </WorkerRole>
                      <WorkerName $isActive={activeWorker === worker}>
                        {t(`digitalWorkers.workers.${worker}.profile.name`)}
                      </WorkerName>
                    </div>
                  </WorkerContent>
                </WorkerCard>
              );
            } catch (error) {
              /* log removed */
              return (
                <div key={worker} style={{ padding: '20px', background: '#ffe6e6' }}>
                  Error: {worker} - {error.message}
                </div>
              );
            }
          })}
        </WorkerSelector>
        
        <AnimatePresence mode="wait">
          {renderWorkerSection(activeWorker)}
        </AnimatePresence>
      </Container>
    </Section>
  );
};

export default DigitalWorkersV2; 